import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Divider, Button, Chip} from 'views/components';
import {Translate, translate} from 'views/module';

const SkillsAndExp = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const [viewQA, setViewQA] = React.useState(false);

  const handleToggleQA = () => {
    setViewQA((prev) => !prev);
  };

  const resolvedPersonality = React.useMemo(() => {
    const personality = bioQuestions?.personality || null;
    const personalitySummary = bioQuestions?.personalitySummary || null;
    const personalityCompleted = Boolean(bioQuestions?.personalityCompleted);
    const hasAnyData = Boolean(personality || personalitySummary);

    if (!hasAnyData) {
      return {
        personality: null,
        personalitySummary: null,
        personalityCompleted: false,
        hasAnyData: false,
      };
    }

    return {
      personality,
      personalitySummary,
      personalityCompleted,
      hasAnyData,
    };
  }, [bioQuestions]);

  React.useEffect(() => {
    setIsComplete(Boolean(resolvedPersonality.personalityCompleted));
  }, [resolvedPersonality]);

  if (!resolvedPersonality.hasAnyData) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.Personality.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  const {personality, personalitySummary} = resolvedPersonality;

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{width: '100%'}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
        <Translate expression="caregiver.CaregiverBiography.Personality.Summary.Title" />
      </Typography>
      <Divider />

      {/* Summary box */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #0288d1',
          borderRadius: '12px',
          backgroundColor: '#e1f5fe',
          paddingY: 1,
          paddingX: 1.5,
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
          <Translate expression="caregiver.CaregiverBiography.Personality.SummarySection.Title" />
        </Typography>

        {/* Descriptor chips */}
        <Stack direction='row' gap={2} sx={{my: 1}}>
          {
            Boolean(personalitySummary?.descriptorChips) &&
            personalitySummary.descriptorChips.map((x, i) =>
              <Chip
                key={i}
                label={x}
                variant='outlined'
                sx={{
                  borderColor: '#0288d1',
                  color: '#0288d1',
                  fontWeight: 500,
                }}
              />)
          }
        </Stack>

        {/* Summaries */}
        <Box>
          <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
            <Translate expression={'caregiver.CaregiverBiography.Personality.SummarySection.Labels.DecisionStyle'} />
          </Typography>
          <Box sx={{ml: 1}}>
            <Typography variant="body2" sx={{color: '#0288d1'}}>
              {personalitySummary?.decisionStyle}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
            <Translate expression={'caregiver.CaregiverBiography.Personality.SummarySection.Labels.CareDrive'} />
          </Typography>
          <Box sx={{ml: 1}}>
            <Typography variant="body2" sx={{color: '#0288d1'}}>
              {personalitySummary?.careDrive}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
            <Translate expression={'caregiver.CaregiverBiography.Personality.SummarySection.Labels.Reliability'} />
          </Typography>
          <Box sx={{ml: 1}}>
            <Typography variant="body2" sx={{color: '#0288d1'}}>
              {personalitySummary?.reliability}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
            <Translate expression={'caregiver.CaregiverBiography.Personality.SummarySection.Labels.OverallSummary'} />
          </Typography>
          <Box sx={{ml: 1}}>
            <Typography variant="body2" sx={{color: '#0288d1'}}>
              {personalitySummary?.overallSummary}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Q&A box */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #0288d1',
          borderRadius: '12px',
          backgroundColor: '#e1f5fe',
          paddingY: 1,
          paddingX: 1.5,
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
          <Translate expression="caregiver.CaregiverBiography.Personality.QASection.Title" />
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={handleToggleQA}
          sx={{
            alignSelf: 'flex-start',
            padding: 0,
            textTransform: 'none',
            minHeight: 0,
            fontSize: '0.8rem',
            color: '#0288d1',
            fontWeight: 500,
          }}
        >
          {
            viewQA ?
            translate('caregiver.CaregiverBiography.Personality.QASection.Actions.Collapse') :
            translate('caregiver.CaregiverBiography.Personality.QASection.Actions.Expand')
          }
        </Button>
        {
          viewQA &&
            <>
              <Divider sx={{borderColor: '#0288d1', my: 1}} />
              {
                Object.entries(personality.questions).map(([k, v]) => {
                  return (
                    <Box key={k}>
                      <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                        <Translate expression={`caregiver.CaregiverBiography.Personality.QASection.Labels.${k}.Prompt`} />
                      </Typography>
                      <Box sx={{ml: 1}}>
                        <Typography variant="body2" sx={{color: '#0288d1'}}>
                          <Translate expression={`caregiver.CaregiverBiography.Personality.QASection.Labels.${k}.Options.${v[0].toUpperCase()+v.slice(1)}`} />
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              }
            </>
        }
      </Box>
    </Stack>
  );
};

SkillsAndExp.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(SkillsAndExp);
